import { queryClientAtom } from "jotai-tanstack-query";
import type { Atom, Getter } from "jotai";

import {
  createBackendUnexpectedFailure,
  type BackendFailure,
} from "#/shared/lib/backend-api";
import type { ResultAsync } from "#/shared/lib/result";

import { createResourceCache } from "./cache";
import type { ResourceQueryReference } from "./definition";
import {
  createResultMutationAtom,
  type ResourceMutation,
} from "./result-mutation";

export interface ResourceMutationDefinition<TData, TVariables, TError> {
  invalidates?:
    | readonly ResourceQueryReference[]
    | ((variables: TVariables) => readonly ResourceQueryReference[]);
  mutationFn: (variables: TVariables) => ResultAsync<TData, TError>;
  normalizeUnexpectedFailure: (cause: unknown) => TError;
  resource:
    | ResourceQueryReference
    | ((variables: TVariables) => ResourceQueryReference);
}

/**
 * 错误契约中 `backend.unexpected` 变体允许的 operation 取值。
 * 契约不含该变体时结果为 `never`，调用点因此无法通过类型检查。
 */
type ResourceMutationOperation<TError> = [
  Extract<TError, BackendFailure>,
] extends [never]
  ? never
  : Extract<TError, BackendFailure> extends BackendFailure<infer Op>
    ? Op
    : never;

/**
 * 逃逸出 action 或生命周期的 rejection 统一归入 `backend.unexpected`，
 * operation 取定义里声明的语义写操作名，因此调用方不再自带 normalizer，
 * 也不需要在调用点写 try/catch。
 */
export function defineResourceMutation<TData, TVariables, TError>(
  definition: Omit<
    ResourceMutationDefinition<TData, TVariables, TError>,
    "normalizeUnexpectedFailure"
  > & {
    operation: ResourceMutationOperation<TError>;
  },
): ResourceMutationDefinition<TData, TVariables, TError>;
export function defineResourceMutation<TData, TVariables, TError>(
  definition: Omit<
    ResourceMutationDefinition<TData, TVariables, TError>,
    "normalizeUnexpectedFailure"
  > & {
    operation: string;
  },
): ResourceMutationDefinition<TData, TVariables, TError> {
  const { operation, ...rest } = definition;

  return {
    ...rest,
    // operation 的类型已保证产出的 backend.unexpected 属于 TError，
    // 但实现签名无法向编译器表达这一依赖，故显式收窄。
    normalizeUnexpectedFailure: (cause) =>
      createBackendUnexpectedFailure(operation, cause) as unknown as TError,
  };
}

export function createResourceMutationAtom<TData, TVariables, TError>(
  mutationOrReader:
    | ResourceMutationDefinition<TData, TVariables, TError>
    | ((get: Getter) => ResourceMutationDefinition<TData, TVariables, TError>),
): Atom<ResourceMutation<TData, TVariables, TError>> {
  return createResultMutationAtom<TData, TVariables, TError, void>((get) => {
    const mutation =
      typeof mutationOrReader === "function"
        ? mutationOrReader(get)
        : mutationOrReader;
    const cache = createResourceCache(get(queryClientAtom));

    return {
      mutationFn: mutation.mutationFn,
      normalizeUnexpectedFailure: mutation.normalizeUnexpectedFailure,
      async onMutate(variables) {
        await cache.cancel(resolveTarget(mutation.resource, variables));
      },
      async onSuccess(_data, variables) {
        const mutationResource = resolveTarget(mutation.resource, variables);
        const invalidates =
          typeof mutation.invalidates === "function"
            ? mutation.invalidates(variables)
            : (mutation.invalidates ?? [mutationResource]);

        await Promise.all(
          invalidates.map((target) => cache.invalidate(target)),
        );
      },
    };
  });
}

function resolveTarget<TVariables>(
  target:
    | ResourceQueryReference
    | ((variables: TVariables) => ResourceQueryReference),
  variables: TVariables,
): ResourceQueryReference {
  return typeof target === "function" ? target(variables) : target;
}
